/*
 * Buyer phone number - required in the cart drawer, the added-to-cart popup
 * and on the cart page, before checkout.
 *
 * WHY
 *   On the Advanced plan, Shopify's checkout only asks for an email under
 *   Contact. The checkout's address phone is the RECIPIENT's number
 *   (relabelled "Recipient phone number" in locales/en-US.json). This field
 *   gets the BUYER's number so the shop can call them about the order.
 *
 * WHERE IT GOES
 *   Cart attribute "Customer phone" -> the order's Additional details (note
 *   attributes). Shopify Flow copies it onto the customer record. Order
 *   Printer reads it as order.attributes['Customer phone']. Never rename it.
 *
 * HOW
 *   The field is injected above the Zapiet picker and checkout buttons whenever wpbingo.js
 *   re-renders the drawer / popup / cart page (same pattern as
 *   cart-card-notes.js). Typing saves quietly to /cart/update.js. A click on
 *   Checkout or an express-pay button with no valid number is stopped and the
 *   field is highlighted. "View cart" is never blocked.
 *   Load this BEFORE cart-card-notes.js so the phone check runs first.
 */
(function () {
	'use strict';

	var ATTR = 'Customer phone';
	var MIN_DIGITS = 10;
	var TYPE_PAUSE = 800;
	var CHECKOUT_SELECTOR = [
		'[name="checkout"]', '.cart-modal__btn--checkout',
		'a[href*="/checkout"]', '.additional_checkout_buttons',
		'[data-shopify="payment-button"]', 'shopify-accelerated-checkout',
		'shopify-accelerated-checkout-cart'
	].join(',');
	// Where to put the field: just above each set of checkout buttons.
	var ANCHORS = '.ajaxcart__footer-buttons, .cart-total__buttons, .cart-modal__funcs';

	var value = '';      // what the customer has typed (survives re-renders)
	var saved = null;    // what the cart currently holds
	var saving = null;   // in-flight save promise
	var timer = null;
	var bypass = null;
	var uid = 0;

	function digits(v) { return String(v || '').replace(/\D/g, ''); }
	function isValid(v) {
		var d = digits(v);
		if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
		return d.length >= MIN_DIGITS && d.length <= 15;
	}

	/* ---------- saving ---------- */

	function save() {
		clearTimeout(timer);
		var v = value.trim();
		if (v === saved) return saving || Promise.resolve();
		var attrs = {};
		attrs[ATTR] = v;
		var p = fetch('/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify({ attributes: attrs })
		}).then(function (r) {
			if (!r.ok) throw new Error('save failed');
			saved = v;
		});
		saving = p.then(function () { saving = null; }, function (e) { saving = null; throw e; });
		return saving;
	}

	/* ---------- UI ---------- */

	function build() {
		var id = 'vb-buyer-phone-' + (++uid);
		var wrap = document.createElement('div');
		wrap.className = 'vb-phone';
		wrap.setAttribute('data-vb-phone', '');
		wrap.innerHTML =
			'<label class="vb-phone__label" for="' + id + '">Your phone number</label>' +
			'<p class="vb-phone__help" id="' + id + '-help">In case we need to contact you about your order.</p>' +
			'<input class="vb-phone__input" id="' + id + '" type="tel" inputmode="tel" autocomplete="tel" ' +
				'maxlength="25" placeholder="e.g. 905-555-0123" aria-describedby="' + id + '-help" aria-required="true">' +
			'<p class="vb-phone__error" role="alert" hidden>Please enter your phone number so we can reach you about this order.</p>';
		var input = wrap.querySelector('input');
		input.value = value;
		input.addEventListener('input', function () {
			value = input.value;
			syncInputs(input);
			clearError();
			clearTimeout(timer);
			timer = setTimeout(function () { save().catch(function () {}); }, TYPE_PAUSE);
		});
		input.addEventListener('blur', function () { save().catch(function () {}); });
		return wrap;
	}

	function fields() { return document.querySelectorAll('[data-vb-phone]'); }

	function syncInputs(except) {
		Array.prototype.forEach.call(fields(), function (f) {
			var i = f.querySelector('input');
			if (i !== except && i.value !== value) i.value = value;
		});
	}

	function showError() {
		var target = null;
		Array.prototype.forEach.call(fields(), function (f) {
			f.classList.add('is-error');
			f.querySelector('.vb-phone__error').hidden = false;
			if (!target && f.offsetParent !== null) target = f;
		});
		if (target) {
			if (target.scrollIntoView) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
			target.querySelector('input').focus({ preventScroll: true });
		}
	}
	function clearError() {
		Array.prototype.forEach.call(fields(), function (f) {
			f.classList.remove('is-error');
			f.querySelector('.vb-phone__error').hidden = true;
		});
	}

	// The field lives INSIDE each button block (so it shares the block's side
	// padding), ABOVE Zapiet's Local Delivery / Store Pickup picker
	// (#storePickupApp): straight before it when Zapiet has injected it,
	// otherwise as the block's first child (Zapiet then adds its picker below,
	// just before the checkout button). Never moved while the customer is
	// typing in it. Never touch #storePickupApp itself - Zapiet only re-injects
	// it when it can't find one.
	function inject(root) {
		var anchors = root.querySelectorAll(ANCHORS);
		Array.prototype.forEach.call(anchors, function (a) {
			var field = a.querySelector('[data-vb-phone]');
			var zap = a.querySelector('#storePickupApp');
			if (!field) field = build();
			else if (field.contains(document.activeElement)) return;
			if (zap) {
				if (zap.previousElementSibling !== field) zap.parentNode.insertBefore(field, zap);
			} else if (a.firstElementChild !== field) {
				a.insertBefore(field, a.firstChild);
			}
		});
	}

	/* ---------- checkout guard ---------- */

	function findAgain(btn) {
		if (btn.isConnected) return btn;
		var all = document.querySelectorAll(CHECKOUT_SELECTOR);
		for (var i = 0; i < all.length; i++) if (all[i].offsetParent !== null) return all[i];
		return null;
	}

	window.addEventListener('click', function (e) {
		var btn = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
		if (!btn) return;
		if (bypass === btn) { bypass = null; return; }

		if (!isValid(value)) {
			e.preventDefault();
			e.stopImmediatePropagation();
			showError();
			return;
		}
		if (value.trim() === saved && !saving) return; // already in the cart

		e.preventDefault();
		e.stopImmediatePropagation();
		save().then(function () {
			var again = findAgain(btn);
			if (!again) return;
			bypass = again;
			again.click();
			setTimeout(function () { bypass = null; }, 1500);
		}, function () {
			showError();
		});
	}, true);

	// The cart page form can also be submitted with Enter.
	document.addEventListener('submit', function (e) {
		var form = e.target;
		if (!form || !form.querySelector || !form.querySelector('[data-vb-phone]')) return;
		var sub = e.submitter;
		if (sub && !sub.matches(CHECKOUT_SELECTOR)) return; // View cart, update, etc.
		if (!sub && !isValid(value)) { e.preventDefault(); showError(); }
	}, true);

	/* ---------- boot ---------- */

	function watch() {
		// One observer on body covers drawer, popup and cart page re-renders.
		inject(document);
		new MutationObserver(function () { inject(document); }).observe(document.body, { childList: true, subtree: true });
	}

	fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
		.then(function (r) { return r.json(); })
		.then(function (cart) {
			var v = (cart.attributes && cart.attributes[ATTR]) || '';
			saved = v;
			if (!value) { value = v; syncInputs(null); }
		})
		.catch(function () {});

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
	else watch();
})();